// src/App.jsx
/**
 * Top-level host application for PurpleDx / pDx web flows.
 *
 * This component assembles the entire SPA shell:
 * - Firebase-backed authentication, storage, and Firestore access.
 * - Public routes such as landing, login, sign-up, notices, and device checks.
 * - Protected patient and clinician routes such as profile, metadata capture,
 *   recording-mode selection, measurement flows, and results.
 * - Session-scoped patient context and cached profile metadata used to keep
 *   clinician workflows isolated per active participant.
 * - Lazy loading for larger route modules so the initial auth and landing
 *   experience stays lightweight.
 *
 * Recording architecture
 * - The host SPA owns navigation, auth, metadata capture, consent, and the
 *   surrounding UI chrome.
 * - Individual measurement experiences such as spirometry and tidal breathing
 *   are loaded via route-level recording routers and remote pod bundles.
 * - Upload progress from those measurement flows is surfaced centrally through
 *   a shared global toast model so uploads remain visible across route changes.
 *
 * Layout notes
 * - The shell uses `100dvh` to avoid mobile browser viewport jumps.
 * - Navigation changes between the public medical-branding header and the
 *   authenticated in-app navigation depending on route context.
 * - Unmatched routes fall back to the landing page so release builds do not
 *   strand users on dead links.
 */

import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

/* ---------------------------------------- FIREBASE */
import { firebaseApp } from "./firebase/firebaseApp";
import { getAuth, signOut } from "firebase/auth";
import { getStorage } from "firebase/storage";
import { getFirestore } from "firebase/firestore";

/* ---------------------------------------- PAGES */
import HomePage from "./pages/HomePage";
import FormPage from "./pages/FormPage";
import QuestionnairesPage from "./pages/QuestionnairesPage";
import LevelSelectionPage from "./pages/LevelSelectionPage";
import ConfirmMetadataPage from "./pages/ConfirmMetadataPage";
import NoticePage from "./pages/NoticePage";
import AboutPage from "./pages/AboutPage";
import ResultsPage from "./pages/ResultsPage";
import ResultPage from "./pages/ResultPage";
/* ---------------------------------------- COMPONENTS */
import NavBar from "./components/navigation/NavBar";
import MDNavBar from "./components/navigation/MDNavBar";
import ForgotPasswordRequest from "./components/authentication/ForgotPasswordRequest";
import ResetPassword from "./components/authentication/ResetPassword";
import { CompatibleRoute } from "./components/navigation/CompatibleRoute";
import GlobalUploadToast from "./components/common/GlobalUploadToast";
import CookieConsentBanner from "./components/consent/CookieConsentBanner";
import AnalyticsGate from "./components/consent/AnalyticsGate";
import { ProtectedRoute } from "./components/authentication/ProtectedRoute";
import ModeRouteGuard, {
  EndSessionOnUnmount,
} from "./components/recording/ModeRouteGuard";
import { MenuPanel } from "./components/navigation/MenuPanel";
import PhoneLandscapeBlocker from "./components/common/PhoneLandscapeBlocker";

/* ---------------------------------------- CONTEXT */
import { useConsent } from "./context/ConsentContext";
import { useAuth } from "./context/AuthContext";
import { useFirstRun } from "./context/FirstRunContext";
import { useProfilePrefetch } from "./context/ProfilePrefetchContext";
import { usePatient } from "./context/PatientContext";

/* ---------------------------------------- UTILS */
import { useSyncHtmlLang } from "./utils/useSyncHtmlLang";
import { useNavVariant } from "./utils/navigation/useNavVariant";
import { analytics, trackPageView } from "./utils/analytics/track";
import { MEASUREMENT_VARIANTS } from "./utils/constants";
import { getGuidePathFromCameraPath } from "./utils/navigation/recordingGuideNav";

/* ---------------------------------------- HOOKS */
import useFirstRunLoginPrefetch from "./hooks/useFirstRunLoginPrefetch";
import useAuthCacheHygiene from "./hooks/useAuthCacheHygiene";
import useProfileLoginPrefetch from "./hooks/useProfileLoginPrefetch";
import useUploadToasts from "./hooks/useUploadToasts";
import useBrowserCompatibility from "./hooks/useBrowserCompatibility";
import { useRecordingModeConfig } from "./hooks/useRecordingModeConfig";
import { useLastSafeRoute } from "./hooks/useLastSafeRoute";

/* ---------------------------------------- ROUTES */
import RecordingSpirometryRouter from "./routes/RecordingSpirometryRouter";
import RecordingTidalBreathingRouter from "./routes/RecordingTidalBreathingRouter";
import RecordingModeGate from "./components/recording/RecordingModeGate";

/* ---------------------------------------- MEASUREMENT SESSION */
import { ActiveMeasurementSessionProvider } from "./recording/session/ActiveMeasurementSessionContext";
import BottomNavBar from "./components/navigation/BottomNavBar";
import Spinner from "./components/Spinner";
import { MobileMenuOverlay } from "./components/navigation/MobileMenuOverlay";
import { normalizeId } from "./utils/patient/patientStoragePath";
import { useUserClinicMapping } from "./hooks/useUserClinicMapping";
import { RECORDING_MODES } from "./utils/recordingModes";

/* ------------------------------------------------------------------ */
/* Lazy-loaded pages (code-splitting)                                 */
/* ------------------------------------------------------------------ */
const LandingPage = lazy(() => import("./pages/LandingPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const CompatibilityPage = lazy(() => import("./pages/CompatibilityPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const BrowserCompatibilityPage = lazy(
  () => import("./pages/BrowserCompatibilityPage"),
);

/* ------------------------------------------------------------------ */

export default function App() {
  const auth = getAuth(firebaseApp);
  const storage = getStorage(firebaseApp);
  const db = getFirestore(firebaseApp);
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const {
    ensureProfile,
    ensureProfileInfo,
    getCachedProfileInfo,
    clearProfile,
    clearAllProfiles,
  } = useProfilePrefetch();

  useProfileLoginPrefetch({
    uid: user?.uid,
    ensureProfileInfo,
    ensureProfile,
    getCachedProfileInfo,
    ttlMs: 10 * 60 * 1000,
    recertMs: 3 * 30 * 24 * 60 * 60 * 1000,
  });

  const { activeId, setActiveId } = usePatient();

  const mapping = useUserClinicMapping({ uid: user?.uid });

  // Cross-device patient sync: Firestore mapping is source of truth
  useEffect(() => {
    if (!user?.uid) return;
    if (!mapping.resolved) return;

    const remote = normalizeId(mapping.id); // "" if null/empty
    const local = normalizeId(activeId);

    // In a fresh Firebase project the denormalised mapping doc may not exist
    // yet, even though the current tab already knows the active patient id.
    // Only let Firestore override local state when it actually has an id.
    if (remote && remote !== local) {
      setActiveId(remote); // will also write sessionStorage (per your PatientProvider)
    }
  }, [user?.uid, mapping.resolved, mapping.id, activeId, setActiveId]);

  const subjectId = useMemo(() => normalizeId(activeId) || null, [activeId]); // normalize once

  const handleSignOut = useCallback(() => {
    try {
      if (clearAllProfiles) clearAllProfiles();
      else if (user?.uid) clearProfile(user.uid);
    } catch {
      // ignore
    }

    signOut(getAuth(firebaseApp))
      .then(() => navigate("/auth/login", { replace: true }))
      .catch(
        (err) => process.env.NODE_ENV === "development" && console.error(err),
      );
  }, [navigate, clearAllProfiles, clearProfile, user?.uid]);

  const {
    loading: loadingModeConfig,
    mappingResolved: mappingResolved,
    clinicStudyId,
    config,
    error: modeConfigError,
  } = useRecordingModeConfig({ uid: user?.uid });

  // IMPORTANT: distinguish "unknown" from "unrestricted"
  const permittedModes = Array.isArray(config?.permittedModes)
    ? config.permittedModes
    : null;
  const fallbackPermittedModes = useMemo(
    () =>
      new Set([
        RECORDING_MODES.PULSE,
        RECORDING_MODES.TIDAL,
        RECORDING_MODES.SPIROMETRY,
      ]),
    [],
  );

  const modesReady = mappingResolved && !loadingModeConfig;

  // Fall back to the core PurpleDx modes when there is no clinic mapping
  // or no explicit allowlist in Firestore.
  const isPermitted = (mode) => {
    if (!modesReady) return false;

    // No clinic mapping yet: allow the default core modes.
    if (!clinicStudyId) return fallbackPermittedModes.has(mode);

    // Explicit allowlist wins when present and non-empty.
    if (permittedModes?.length) return permittedModes.includes(mode);

    // Missing/empty config: fall back to the core modes instead of failing closed.
    return fallbackPermittedModes.has(mode);
  };

  const { ensureFirstRun, getCachedFirstRun, clearAllFirstRun } = useFirstRun();

  useFirstRunLoginPrefetch({
    uid: user?.uid,
    db,
    ensureFirstRun,
    variants: MEASUREMENT_VARIANTS,
    ttlMs: 10 * 60 * 1000,
    // requireCompleted: true, // enable if you store status == "completed"
  });

  const firstRunEntry = user?.uid ? getCachedFirstRun(user.uid) : null;
  // safest defaults while loading: gate (true)
  const firstRunByVariant =
    firstRunEntry?.status === "ready" && firstRunEntry?.value?.byVariant
      ? firstRunEntry.value.byVariant
      : Object.fromEntries(MEASUREMENT_VARIANTS.map((v) => [v, true]));

  useAuthCacheHygiene({
    uid: user?.uid,
    clearProfile,
    clearAllProfiles,
    clearAllFirstRun,
  });

  useSyncHtmlLang();
  const params = new URLSearchParams(window.location.search);
  const forceCompat = params.get("forceCompat"); // e.g. in_app_browser | webview | safari_webkit | any

  const detectedBrowserInfo = useBrowserCompatibility(); // always call hook

  const forcedInfo = forceCompat
    ? {
        isSupported: false,
        reasons: [forceCompat],
        browser: "Forced",
        context: "forced",
      }
    : null;

  const browserInfo = forcedInfo || detectedBrowserInfo;

  // Enforce on deep-link sensitive entry points (QR / recording flows)
  const pathname = window.location.pathname || "";
  const isSensitiveEntry =
    pathname.startsWith("/recording") || pathname.startsWith("/auth");

  const shouldShowBrowserWarning =
    isSensitiveEntry &&
    !browserInfo.isSupported &&
    !pathname.startsWith("/compatibility"); // avoid loops

  // Decide which nav bar variant to render based on location auth state.
  const navVariant = useNavVariant(); // 'main' | 'landing'

  const { toastModel, onUploadStatus, onUploadProgress } = useUploadToasts();
  // const toastModel = {
  //   isOpen: true,
  //   percent: 42,
  //   status: "uploading", // "paused" | "success" | "error"
  //   count: 1,
  // };
  const location = useLocation();
  const consent = useConsent();

  useEffect(() => {
    if (consent?.status === "set" && consent?.analytics) {
      trackPageView(location.pathname + location.search);
    }
  }, [location.pathname, location.search, consent?.status, consent?.analytics]);

  const guidePath = useMemo(() => {
    return getGuidePathFromCameraPath(location?.pathname || "");
  }, [location?.pathname]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const path = location?.pathname || "";
  const isRecordingRoute = path.startsWith("/recording");
  const isFormRoute = path.startsWith("/form");

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  useLastSafeRoute();

  return (
    <ActiveMeasurementSessionProvider>
      <div className="h-[100dvh] flex flex-col typography">
        <PhoneLandscapeBlocker />
        {navVariant === "main" && (
          <NavBar
            menuOpen={menuOpen}
            onToggleMenu={() => setMenuOpen((o) => !o)}
            onCloseMenu={() => setMenuOpen(false)}
            guidePath={guidePath}
            handleSignOut={handleSignOut}
          />
        )}

        {/* Consent banner (shown only when status !== 'set') */}
        <CookieConsentBanner />

        {/* Loads analytics only after analytics consent */}
        <AnalyticsGate />

        <GlobalUploadToast toastModel={toastModel} />

        {/* Suspense boundary – shows fallback while lazy chunks download */}
        <div className="flex flex-col items-center justify-start h-full overflow-y-scroll">
          <Suspense
            fallback={
              <div className="m-auto">
                <Spinner />
              </div>
            }
          >
            {shouldShowBrowserWarning ? (
              <BrowserCompatibilityPage info={browserInfo} />
            ) : (
              <Routes>
                {/* ---------- Public routes ---------- */}
                <Route path="/" element={<LandingPage />} />
                <Route
                  path="/auth/*"
                  // element={<AuthPage />}
                  element={<AuthPage />}
                />
                <Route
                  path="/auth/forgot-password"
                  element={<ForgotPasswordRequest />}
                />
                <Route
                  path="/auth/reset-password"
                  element={<ResetPassword />}
                />
                <Route path="/about" element={<AboutPage />} />
                {/* ---------- Protected routes ---------- */}
                <Route
                  path="/home"
                  element={
                    <ProtectedRoute>
                      <HomePage
                        firstRunByVariant={firstRunByVariant}
                        isPermitted={isPermitted}
                        loadingModes={!modesReady}
                      />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/results"
                  element={
                    <ProtectedRoute>
                      <ResultsPage db={db} user={user} />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/result"
                  element={
                    <ProtectedRoute>
                      <ResultPage db={db} user={user} />
                    </ProtectedRoute>
                  }
                />
                {/* 
                <Route
                  path="/questionnaires/:questionnaireName?"
                  element={
                    <ProtectedRoute>
                      <QuestionnairesPage />
                    </ProtectedRoute>
                  }
                /> */}

                <Route
                  path="/notice?"
                  element={
                    <ProtectedRoute>
                      <NoticePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/form"
                  element={
                    <ProtectedRoute>
                      <FormPage />
                    </ProtectedRoute>
                  }
                />

                <Route path="/about" element={<AboutPage />} />

                <Route
                  path="/recording"
                  element={
                    <CompatibleRoute>
                      <ProtectedRoute>
                        <LevelSelectionPage
                          firstRunByVariant={firstRunByVariant}
                          isPermitted={isPermitted}
                          loadingModes={!modesReady}
                        />
                      </ProtectedRoute>
                    </CompatibleRoute>
                  }
                />

                {/* --- MODE GUARDED ROUTES --- */}
                <Route
                  path="/recording/spirometry/*"
                  element={
                    <CompatibleRoute>
                      <ProtectedRoute>
                        {/* <RecordingModeGate
                          allowed={isPermitted("spirometry")}
                          defaultMode={config?.defaultMode}
                        > */}
                        <ModeRouteGuard mode="spirometry">
                          <EndSessionOnUnmount reason="left_spirometry">
                            <RecordingSpirometryRouter
                              auth={auth}
                              storage={storage}
                              db={db}
                              user={user}
                              firstRun={
                                true
                                // !!firstRunByVariant["RecordingSpirometry"]
                              }
                              onUploadStatus={onUploadStatus}
                              onUploadProgress={onUploadProgress}
                              analytics={analytics}
                              subjectId={subjectId}
                              storagePathOverride={
                                subjectId
                                  ? `purpleDx_6.0.0/Addenbrookes_NOV_2025/${subjectId}/videos`
                                  : null
                              }
                              filenameStyle="timestamp"
                              appMode={"spirometry"}
                              videoRetentionMode="full-frame"
                            />
                          </EndSessionOnUnmount>
                        </ModeRouteGuard>
                        {/* </RecordingModeGate> */}
                      </ProtectedRoute>
                    </CompatibleRoute>
                  }
                />

                <Route
                  path="/recording/tidal-breathing/*"
                  element={
                    <CompatibleRoute>
                      <ProtectedRoute>
                        {/* <RecordingModeGate
                          allowed={isPermitted("tidal-breathing")}
                          defaultMode={config?.defaultMode}
                        > */}
                        <ModeRouteGuard mode="tidal-breathing">
                          <EndSessionOnUnmount reason="left_tidal_breathing">
                            <RecordingTidalBreathingRouter
                              auth={auth}
                              storage={storage}
                              db={db}
                              user={user}
                              firstRun={
                                true
                                // !!firstRunByVariant["RecordingTidalBreathing"]
                              }
                              onUploadStatus={onUploadStatus}
                              onUploadProgress={onUploadProgress}
                              analytics={analytics}
                              variant={"RecordingTidalBreathing"}
                              showLiveChart={false}
                              appMode={"respiratory"}
                              subjectId={subjectId}
                              enablePixelSignal={true}
                              enableGrid3x3Pixel={false}
                              videoRetentionMode="full-frame"
                            />
                          </EndSessionOnUnmount>
                        </ModeRouteGuard>
                        {/* </RecordingModeGate> */}
                      </ProtectedRoute>
                    </CompatibleRoute>
                  }
                />

                <Route
                  path="/recording/pulse-scan/*"
                  element={
                    <CompatibleRoute>
                      <ProtectedRoute>
                        {/* <RecordingModeGate
                          allowed={isPermitted("pulse-scan")}
                          defaultMode={config?.defaultMode}
                        > */}
                        <ModeRouteGuard mode="pulse-scan">
                          <EndSessionOnUnmount reason="left_pulse_scan">
                            <RecordingTidalBreathingRouter
                              auth={auth}
                              storage={storage}
                              db={db}
                              user={user}
                              firstRun={
                                true
                                // !!firstRunByVariant["RecordingTidalBreathing"]
                              }
                              onUploadStatus={onUploadStatus}
                              onUploadProgress={onUploadProgress}
                              analytics={analytics}
                              showLiveChart={false}
                              variant={"RecordingTidalBreathing"}
                              appMode={"pulse"}
                              subjectId={subjectId}
                              enablePixelSignal={false}
                              enableGrid3x3Pixel={false}
                              poseDebug={false}
                              videoRetentionMode="full-frame"
                            />
                          </EndSessionOnUnmount>
                        </ModeRouteGuard>
                        {/* </RecordingModeGate> */}
                      </ProtectedRoute>
                    </CompatibleRoute>
                  }
                />

                {/* 
                <Route
                  path="/recording/manual-audio/*"
                  element={
                    <CompatibleRoute>
                      <ProtectedRoute>
                        <RecordingModeGate
                          allowed={isPermitted("manual-audio")}
                          defaultMode={config?.defaultMode}
                        >
                          <ModeRouteGuard mode="manual-audio">
                            <EndSessionOnUnmount reason="left_manual_audio">
                              <RecordingManualAudioRouter
                                auth={auth}
                                storage={storage}
                                db={db}
                                user={user}
                                firstRun={
                                  !!firstRunByVariant["RecordingManualAudio"]
                                }
                                onUploadStatus={onUploadStatus}
                                onUploadProgress={onUploadProgress}
                                analytics={analytics}
                                variant={"RecordingManualAudio"}
                              />{" "}
                            </EndSessionOnUnmount>
                          </ModeRouteGuard>
                        </RecordingModeGate>
                      </ProtectedRoute>
                    </CompatibleRoute>
                  }
                />

                <Route
                  path="/recording/post-exertion/*"
                  element={
                    <CompatibleRoute>
                      <ProtectedRoute>
                        <RecordingModeGate
                          allowed={isPermitted("post-exertion")}
                          defaultMode={config?.defaultMode}
                        >
                          <ModeRouteGuard mode="post-exertion">
                            <EndSessionOnUnmount reason="left_post_exertion">
                              <RecordingPostExertionRouter
                                auth={auth}
                                storage={storage}
                                db={db}
                                user={user}
                                firstRun={
                                  !!firstRunByVariant["RecordingPostExertion"]
                                }
                                onUploadStatus={onUploadStatus}
                                onUploadProgress={onUploadProgress}
                                analytics={analytics}
                                variant={"RecordingPostExertion"}
                              />{" "}
                            </EndSessionOnUnmount>
                          </ModeRouteGuard>
                        </RecordingModeGate>
                      </ProtectedRoute>
                    </CompatibleRoute>
                  }
                /> */}

                {/* <Route
                    path="/review-metadata"
                    element={
                      <ProtectedRoute>
                        <ConfirmMetadataPage />
                      </ProtectedRoute>
                    }
                  /> */}

                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <ProfilePage handleSignOut={handleSignOut} />
                    </ProtectedRoute>
                  }
                />

                <Route path="/compatibility" element={<CompatibilityPage />} />

                {/* ---------- Fallback ---------- */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            )}
          </Suspense>
        </div>
        {menuOpen && (
          <>
            {/* Mobile overlay */}
            <MobileMenuOverlay
              onClose={() => setMenuOpen(false)}
              guidePath={guidePath}
              handleSignOut={handleSignOut}
            />

            {/* Desktop popdown */}
            <div className="hidden md:block fixed top-[44px] left-0 right-0 z-40">
              {/* optional backdrop just behind dropdown */}
              <button
                type="button"
                className="absolute inset-0 h-[calc(100vh-44px)] bg-transparent"
                aria-label="Close menu"
                onClick={() => setMenuOpen(false)}
              />

              <div className="relative mx-auto w-full flex justify-center bg-container border-b border-greyscale-3  shadow-lg p-4">
                <MenuPanel
                  onClose={() => setMenuOpen(false)}
                  handleSignOut={handleSignOut}
                  guidePath={guidePath}
                  dense
                />
              </div>
            </div>
          </>
        )}
        {navVariant === "main" && !isRecordingRoute && !isFormRoute ? (
          <BottomNavBar
            menuOpen={menuOpen}
            onToggleMenu={() => setMenuOpen((o) => !o)}
            onCloseMenu={() => setMenuOpen(false)}
          />
        ) : null}
      </div>{" "}
    </ActiveMeasurementSessionProvider>
  );
}
